import { Check, Hammer, Heart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
	ExampleTag,
	PhoneFrame,
	shotProps,
} from "@/components/blocks/frames/Frames";
import { DURATION, EASE, SPRING } from "@/components/motion/motion";
import { usePlayOnce } from "@/hooks/use-play-once";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/*
 * Chouic case study scenes. The game screens borrow the backgrounds of
 * Chouic's apps (`ch-*` tokens); the Lezo signal keeps marking the path
 * (migrated, validated, released).
 */

const GAMES = [
	{
		key: "couple",
		screen: "bg-linear-to-br from-ch-yellow to-ch-amber text-ch-ink",
		pill: "bg-white/45",
		tilt: -7,
	},
	{
		key: "dare",
		screen: "bg-linear-to-br from-ch-blue to-ch-navy text-white",
		pill: "bg-white/15",
		tilt: 0,
	},
	{
		key: "friends",
		screen: "bg-linear-to-br from-ch-lime to-ch-green text-ch-ink",
		pill: "bg-white/40",
		tilt: 7,
	},
] as const;

type GameKey = (typeof GAMES)[number]["key"];

/** A party game screen: game name, the prompt card and two answers. */
function GameScreen({ game }: { game: (typeof GAMES)[number] }) {
	const { dict } = useI18n();
	const copy = dict.chouicCase.scenes.phones[game.key as GameKey];

	return (
		<div className={cn("absolute inset-0 flex flex-col p-2.5", game.screen)}>
			<span
				className={cn(
					"self-start rounded-full px-2 py-0.5 text-[8px] font-bold tracking-[0.04em] uppercase",
					game.pill,
				)}
			>
				{copy.game}
			</span>
			<p className="my-auto rounded-xl bg-white p-2.5 text-[10px] leading-snug font-semibold text-balance text-ch-ink shadow-[0_8px_20px_-10px_oklch(0_0_0/0.45)]">
				{copy.prompt}
			</p>
			<div className="grid grid-cols-2 gap-1.5">
				{[copy.a, copy.b].map((answer) => (
					<span
						key={answer}
						className="rounded-lg bg-white/85 py-1.5 text-center text-[9px] font-bold text-ch-ink"
					>
						{answer}
					</span>
				))}
			</div>
		</div>
	);
}

/** Stack chip above each phone: legacy React Native, then Expo. */
function StackChip({ migrated }: { migrated: boolean }) {
	const { dict } = useI18n();
	const scenes = dict.chouicCase.scenes;

	return (
		<div className="flex h-7 items-center justify-center">
			<AnimatePresence mode="popLayout" initial={false}>
				{migrated ? (
					<motion.span
						key="expo"
						initial={{ opacity: 0, y: 10, scale: 0.8 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: -10 }}
						transition={SPRING}
						className="inline-flex items-center gap-1 rounded-full bg-signal px-2.5 py-1 font-mono text-[10px] font-semibold text-signal-foreground"
					>
						<Check className="size-3" strokeWidth={3} />
						{scenes.expo}
					</motion.span>
				) : (
					<motion.span
						key="legacy"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10, scale: 0.9 }}
						transition={{ duration: DURATION.state, ease: EASE }}
						className="inline-flex items-center rounded-full border border-foreground/20 px-2.5 py-1 font-mono text-[10px] text-muted-foreground line-through decoration-foreground/40"
					>
						{scenes.legacy}
					</motion.span>
				)}
			</AnimatePresence>
		</div>
	);
}

const HERO_DURATIONS = [1500, 1200, 1200, 3600] as const;

/**
 * Three party games of a studio's portfolio, migrated one after the other:
 * each chip flips from React Native to Expo and the phone refreshes.
 * The server renders the three apps on Expo.
 */
export function ChouicHeroScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const { ref, index } = useSceneLoop(HERO_DURATIONS, { initial: 3 });

	return (
		<div ref={ref} className={cn("flex flex-col items-center", className)}>
			<div className="flex items-end justify-center">
				{GAMES.map((game, position) => {
					const migrated = index > position;
					return (
						<div
							key={game.key}
							className={cn(
								"relative w-[6.9rem] sm:w-[9rem] lg:w-[9.6rem]",
								position === 1 ? "z-10 -mx-3 mb-8 sm:-mx-4" : "z-0",
							)}
						>
							<StackChip migrated={migrated} />
							<motion.div
								className="mt-2"
								style={{ rotate: game.tilt }}
								initial={false}
								animate={{ y: migrated ? -6 : 0 }}
								transition={SPRING}
							>
								<PhoneFrame className="rounded-[1.6rem] p-[4px] [&>div]:rounded-[1.35rem] [&>div>div:first-child]:h-6 [&>div>div:first-child]:text-[8px] [&>div>div:last-child]:h-3 [&>div>div:last-child>span]:w-10">
									<GameScreen game={game} />
									<AnimatePresence initial={false}>
										{migrated ? (
											<motion.span
												key="sheen"
												aria-hidden="true"
												className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-white/45"
												initial={{ x: "0%", opacity: 1 }}
												animate={{ x: "400%", opacity: 0 }}
												transition={{ duration: 0.9, ease: EASE }}
											/>
										) : null}
									</AnimatePresence>
								</PhoneFrame>
							</motion.div>
						</div>
					);
				})}
			</div>
			<ExampleTag className="mt-6">{dict.common.illustrationBadge}</ExampleTag>
		</div>
	);
}

const QUIZ_DURATIONS = [2200, 2000, 3600] as const;
const BURST = [0, 45, 90, 135, 180, 225, 270, 315] as const;

/**
 * A couple quiz on a phone: the question, one partner answers, then both
 * give the same answer and the chemistry score rises. Server renders the match.
 */
export function CoupleQuizScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const quiz = dict.chouicCase.scenes.quiz;
	const { ref, index } = useSceneLoop(QUIZ_DURATIONS, { initial: 2 });
	const matched = index === 2;
	const players = [
		{ key: "a", name: quiz.players.a, answered: index >= 1 },
		{ key: "b", name: quiz.players.b, answered: matched },
	];

	return (
		<div ref={ref} className={cn("flex justify-center", className)}>
			<PhoneFrame
				tag={dict.common.illustrationBadge}
				className="w-[15.5rem] sm:w-[17rem]"
			>
				<div className="absolute inset-0 flex flex-col gap-3 bg-linear-to-b from-ch-yellow to-ch-amber p-3.5 text-ch-ink">
					<div className="flex items-center justify-between">
						<span className="rounded-full bg-white/50 px-2.5 py-0.5 text-[10px] font-bold tracking-[0.04em] uppercase">
							{quiz.game}
						</span>
						<span className="font-mono text-[10px] font-semibold tabular-nums">
							{quiz.round}
						</span>
					</div>

					<div className="flex items-center gap-2">
						<Heart className="size-3.5 fill-ch-orange text-ch-orange" />
						<span className="text-[10px] font-semibold">{quiz.score}</span>
						<span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/50">
							<motion.span
								className="block h-full origin-left rounded-full bg-ch-orange"
								initial={false}
								animate={{ scaleX: matched ? 0.72 : 0.6 }}
								transition={{ ...SPRING, delay: matched ? 0.35 : 0 }}
							/>
						</span>
					</div>

					<p className="rounded-2xl bg-white p-4 text-[14px] leading-snug font-semibold text-balance shadow-[0_10px_24px_-14px_oklch(0_0_0/0.5)]">
						{quiz.question}
					</p>

					<div className="grid grid-cols-2 gap-2">
						{[quiz.players.a, quiz.players.b].map((answer, position) => {
							const chosen = matched && position === 1;
							return (
								<motion.span
									key={answer}
									className={cn(
										"rounded-xl border-2 py-2.5 text-center text-[13px] font-bold",
										chosen
											? "border-ch-ink bg-ch-ink text-white"
											: "border-white/70 bg-white/45",
									)}
									initial={false}
									animate={{ scale: chosen ? [1, 1.06, 1] : 1 }}
									transition={{ duration: 0.4, ease: EASE }}
								>
									{answer}
								</motion.span>
							);
						})}
					</div>

					<ul className="space-y-2">
						{players.map((player) => (
							<li
								key={player.key}
								className="flex items-center gap-2.5 rounded-xl bg-white/60 px-2.5 py-2"
							>
								<span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-ch-ink text-[11px] font-bold text-white">
									{player.name.charAt(0)}
								</span>
								<span className="flex-1 text-[12px] font-semibold">
									{player.name}
								</span>
								<AnimatePresence mode="popLayout" initial={false}>
									{player.answered ? (
										<motion.span
											key="answer"
											initial={{ opacity: 0, scale: 0.6 }}
											animate={{ opacity: 1, scale: 1 }}
											exit={{ opacity: 0 }}
											transition={SPRING}
											className="rounded-full bg-ch-ink px-2.5 py-1 text-[10px] font-bold text-white"
										>
											{quiz.players.b}
										</motion.span>
									) : (
										<motion.span
											key="waiting"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											className="flex gap-1"
											aria-hidden="true"
										>
											{[0, 1, 2].map((dot) => (
												<span
													key={dot}
													className="size-1.5 animate-pulse rounded-full bg-ch-ink/50 motion-reduce:animate-none"
													style={{ animationDelay: `${dot * 0.18}s` }}
												/>
											))}
										</motion.span>
									)}
								</AnimatePresence>
							</li>
						))}
					</ul>

					<div className="relative mt-auto min-h-12">
						<AnimatePresence mode="wait" initial={false}>
							{matched ? (
								<motion.div
									key="match"
									initial={{ opacity: 0, y: 12, scale: 0.95 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: -8 }}
									transition={SPRING}
									className="relative flex items-center justify-between rounded-xl bg-ch-ink px-3.5 py-3 text-white"
								>
									<span className="text-[13px] font-bold">{quiz.match}</span>
									<span className="font-mono text-[13px] font-semibold text-ch-yellow">
										{quiz.points}
									</span>
									{BURST.map((angle) => (
										<motion.span
											key={angle}
											aria-hidden="true"
											className="absolute top-1/2 left-1/2 size-2 rounded-full bg-ch-orange motion-reduce:hidden"
											initial={{ x: 0, y: 0, opacity: 1, scale: 0.4 }}
											animate={{
												x: Math.cos((angle * Math.PI) / 180) * 90,
												y: Math.sin((angle * Math.PI) / 180) * 34,
												opacity: 0,
												scale: 1,
											}}
											transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
										/>
									))}
								</motion.div>
							) : (
								<motion.p
									key="waiting"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className="py-3 text-center text-[11px] font-medium text-ch-ink/70"
								>
									{index === 1 ? quiz.waiting : " "}
								</motion.p>
							)}
						</AnimatePresence>
					</div>

					<div aria-hidden="true" className="flex gap-1">
						{Array.from({ length: 10 }, (_, round) => (
							<span
								key={round}
								className={cn(
									"h-1 flex-1 rounded-full",
									round < 4 ? "bg-ch-ink" : "bg-white/50",
								)}
							/>
						))}
					</div>
				</div>
			</PhoneFrame>
		</div>
	);
}

/** Bars in % of the track: four weeks then a "later" column (20% each). */
const TIMELINE = [
	{ key: "a", left: 0, width: 30, by: "lezo" },
	{ key: "b", left: 25, width: 30, by: "lezo" },
	{ key: "c", left: 50, width: 30, by: "lezo" },
	{ key: "d", left: 83, width: 15, by: "client" },
] as const;
const BAR_DELAYS = [0.3, 0.95, 1.6, 2.6] as const;
const BAR_GROW = { duration: 0.9, ease: EASE } as const;
const INSTANT = { duration: 0 } as const;

/**
 * The engagement on a calendar: three apps migrated in overlapping slots over
 * four weeks, then a fourth migrated later by the client's team. Plays once;
 * server and reduced motion render the full schedule.
 */
export function MigrationTimeline({ className }: { className?: string }) {
	const { dict } = useI18n();
	const copy = dict.chouicCase.scenes.timeline;
	const { ref, rewound } = usePlayOnce<HTMLElement>();
	const columns = [
		copy.weeks.a,
		copy.weeks.b,
		copy.weeks.c,
		copy.weeks.d,
		copy.after,
	];

	return (
		<figure
			ref={ref}
			className={cn("rounded-3xl border bg-card p-5 md:p-8", className)}
		>
			<div className="flex flex-wrap items-start justify-between gap-4">
				<figcaption className="font-mono text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
					{copy.title}
				</figcaption>
				<ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
					<li className="flex items-center gap-2">
						<span className="h-2 w-4 rounded-full bg-signal" />
						{copy.byLezo}
					</li>
					<li className="flex items-center gap-2">
						<span className="h-2 w-4 rounded-full bg-ch-orange" />
						{copy.byClient}
					</li>
				</ul>
			</div>

			<div className="mt-8 grid gap-x-6 sm:grid-cols-[9rem_minmax(0,1fr)]">
				<div aria-hidden="true" className="hidden sm:block" />
				<div
					aria-hidden="true"
					className="grid grid-cols-5 border-b pb-2 font-mono text-[11px] text-muted-foreground"
				>
					{columns.map((column, position) => (
						<span
							key={column}
							className={cn(
								"pl-2",
								position === 4 && "border-l border-dashed border-foreground/20",
							)}
						>
							{column}
						</span>
					))}
				</div>

				{TIMELINE.map((row, position) => {
					const delay = BAR_DELAYS[position];
					const lezo = row.by === "lezo";
					return (
						<div key={row.key} className="contents">
							<div className="mt-5 sm:mt-0 sm:flex sm:flex-col sm:justify-center sm:py-3">
								<p className="text-sm font-semibold">{copy.apps[row.key]}</p>
								<p className="text-xs text-muted-foreground">
									{lezo ? copy.byLezo : copy.byClient}
								</p>
							</div>
							<div
								aria-hidden="true"
								className="relative mt-2 h-9 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px)] bg-size-[20%_100%] sm:mt-0 sm:h-14"
							>
								<div
									className="absolute top-1/2 h-4 -translate-y-1/2 sm:h-5"
									style={{ left: `${row.left}%`, width: `${row.width}%` }}
								>
									<motion.div
										className={cn(
											"flex h-full origin-left overflow-hidden rounded-full",
											lezo ? "bg-signal" : "bg-ch-orange",
										)}
										initial={false}
										animate={{ scaleX: rewound ? 0 : 1 }}
										transition={rewound ? INSTANT : { ...BAR_GROW, delay }}
									>
										{lezo ? (
											<>
												<span className="w-[22%] bg-background/45" />
												<span className="flex-1" />
												<span className="w-[18%] bg-foreground/20" />
											</>
										) : null}
									</motion.div>
									<motion.span
										className={cn(
											"absolute top-1/2 -right-3 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border-2 border-card",
											lezo
												? "bg-signal text-signal-foreground"
												: "bg-ch-orange text-white",
										)}
										initial={false}
										animate={{ scale: rewound ? 0 : 1 }}
										transition={
											rewound
												? INSTANT
												: { ...SPRING, delay: delay + BAR_GROW.duration - 0.1 }
										}
									>
										<Check className="size-3.5" strokeWidth={3} />
									</motion.span>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			<div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4 text-xs text-muted-foreground">
				<span className="flex items-center gap-2">
					<span className="h-2 w-4 rounded-full bg-signal/55" />
					{copy.phases.diagnostic}
				</span>
				<span className="flex items-center gap-2">
					<span className="h-2 w-4 rounded-full bg-signal" />
					{copy.phases.migration}
				</span>
				<span className="flex items-center gap-2">
					<span className="h-2 w-4 rounded-full bg-[color-mix(in_oklch,var(--color-signal),var(--color-foreground)_20%)]" />
					{copy.phases.release}
				</span>
				<span className="flex items-center gap-2">
					<Check className="size-3.5 text-signal-ink" strokeWidth={3} />
					{copy.done}
				</span>
			</div>
			<p className="mt-3 text-xs text-pretty text-muted-foreground">
				{copy.note}
			</p>
		</figure>
	);
}

const FEED_KEYS = ["mode", "pack", "sdk", "friends"] as const;
/** Each release: building, then released. */
const FEED_DURATIONS = [
	1300, 2300, 1300, 2300, 1300, 2300, 1300, 2300,
] as const;

/**
 * Release history of a game app once on Expo: a new update builds with EAS,
 * gets released and pushes the previous ones down. Server renders it released.
 */
export function ReleaseFeedScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const feed = dict.chouicCase.scenes.feed;
	const { ref, index } = useSceneLoop(FEED_DURATIONS, { initial: 7 });
	const newest = Math.floor(index / 2);
	const building = index % 2 === 0;
	const visible = [0, 1, 2].map(
		(offset) =>
			FEED_KEYS[(newest - offset + FEED_KEYS.length) % FEED_KEYS.length],
	);

	return (
		<div
			ref={ref}
			{...shotProps}
			className={cn(
				"paper shot-shadow w-full max-w-md rounded-2xl border bg-background p-5 select-none",
				className,
			)}
		>
			<div className="mb-4 flex items-center justify-between gap-3">
				<p className="text-sm font-semibold">{feed.title}</p>
				<ExampleTag>{dict.common.illustrationBadge}</ExampleTag>
			</div>
			<ol className="space-y-2">
				<AnimatePresence initial={false} mode="popLayout">
					{visible.map((key, position) => {
						const isBuilding = position === 0 && building;
						return (
							<motion.li
								layout
								key={key}
								initial={{ opacity: 0, y: -16, scale: 0.97 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={{ opacity: 0, scale: 0.97 }}
								transition={SPRING}
								className={cn(
									"flex items-center gap-3 rounded-xl border px-3.5 py-3",
									position === 0 ? "bg-card" : "bg-muted/50",
								)}
							>
								<span
									className={cn(
										"flex size-8 shrink-0 items-center justify-center rounded-lg",
										isBuilding
											? "bg-muted text-muted-foreground"
											: "bg-signal/15 text-signal-ink",
									)}
								>
									{isBuilding ? (
										<Hammer className="size-4" />
									) : (
										<Check className="size-4" strokeWidth={3} />
									)}
								</span>
								<span className="min-w-0 flex-1">
									<span className="block truncate text-sm font-medium">
										{feed.items[key]}
									</span>
									<span className="block font-mono text-[10px] text-muted-foreground">
										{feed.stores}
									</span>
								</span>
								{isBuilding ? (
									<span className="w-20 shrink-0">
										<span className="block text-right font-mono text-[10px] text-muted-foreground">
											{feed.build}
										</span>
										<span className="mt-1 block h-1 overflow-hidden rounded-full bg-muted">
											<motion.span
												className="block h-full origin-left rounded-full bg-signal"
												initial={{ scaleX: 0 }}
												animate={{ scaleX: 1 }}
												transition={{ duration: 1.15, ease: "linear" }}
											/>
										</span>
									</span>
								) : (
									<span className="shrink-0 rounded-full bg-signal/15 px-2 py-0.5 font-mono text-[10px] font-medium text-signal-ink">
										{feed.published}
									</span>
								)}
							</motion.li>
						);
					})}
				</AnimatePresence>
			</ol>
		</div>
	);
}
